import { getOrganicProductCount } from "../../common/CriteoAdvertsForRecs";
import { APP, PLACEMENT } from "@asosteam/asos-web-adverts-sdk";
import { logError, ERROR_TYPE } from "../utils/newRelicLogger";

const normalizeCriteoResponse = (response = {}) => {
  if (Array.isArray(response?.products)) {
    return response;
  }

  const placements = Array.isArray(response?.placements)
    ? response.placements
    : [];

  for (const placementGroup of placements) {
    if (!placementGroup || typeof placementGroup !== "object") {
      continue;
    }

    for (const placementValue of Object.values(placementGroup)) {
      if (!Array.isArray(placementValue)) {
        continue;
      }

      const sponsoredPlacement = placementValue.find((item) =>
        Array.isArray(item?.products),
      );

      if (sponsoredPlacement) {
        return {
          products: sponsoredPlacement.products,
          OnLoadBeacon: sponsoredPlacement.OnLoadBeacon,
          OnViewBeacon: sponsoredPlacement.OnViewBeacon,
          responseId: response.responseId || response["page-uid"],
        };
      }
    }
  }

  return response;
};

const ensureMockLineItemIds = (response = {}) => {
  if (!Array.isArray(response?.products)) {
    return response;
  }

  return {
    ...response,
    products: response.products.map((product, index) => {
      if (product?.LineItemID) {
        return product;
      }

      return {
        ...product,
        LineItemID: `mock-line-item-${product?.ProductId || index}`,
      };
    }),
  };
};

const criteoApi = async (criteoRequiredParams) => {
  const {
    productIdCollection,
    productId,
    isInStock,
    price,
    advertsSdkInstance,
    adsConfig,
    isCriteoBannerBucket = false,
  } = criteoRequiredParams;

  const PAGE_NUMBER = 1;

  if (!productIdCollection?.length || !advertsSdkInstance?.getAdverts)
    return {};

  const {
    current: { value },
  } = price;

  const listSize = getOrganicProductCount(productIdCollection, adsConfig);
  const productIdCollectionPiped = productIdCollection.join("|");

  const parameters = {
    "page-number": PAGE_NUMBER,
    "list-size": listSize,
    "parent-item": productId,
    "item-whitelist": productIdCollectionPiped,
    // Only request Criteo preferred deals (commerce display / BDSS) when the
    // session is in the Criteo banner bucket. In the Google-ad or no-ad
    // buckets we send nocall=pd so Criteo does not return display ads we will
    // never paint, which would otherwise count against the painted rate.
    nocall: isCriteoBannerBucket ? "" : "pd",
    "list-price": value || 0,
    filters: "",
    availability: Number(isInStock),
    price: value || 0,
  };

  const options = {
    app: APP.PDP,
    placement: PLACEMENT.SPONSORED_PRODUCTS,
    requestDisplayAdverts: true,
    requestSponsoredProducts: true,
    breakCache: true,
  };

  const mockOverride =
    window?.asos?.pdp?.config?.mockSponsoredYmal ||
    (() => {
      try {
        const stored = sessionStorage.getItem("mockSponsoredYmal");
        return stored ? JSON.parse(stored) : null;
      } catch {
        return null;
      }
    })();

  if (mockOverride) {
    return ensureMockLineItemIds(normalizeCriteoResponse(mockOverride));
  }

  try {
    const response = await advertsSdkInstance.getAdverts({
      options,
      parameters,
    });

    return normalizeCriteoResponse(response);
  } catch (error) {
    logError(ERROR_TYPE.NonCriticalAppError, {
      message: error.message,
      action: "CriteoAdvertCall",
    });
    return {};
  }
};

export default criteoApi;
